/**
 * SuspensionAppealModal — the paused crew member's screen. Opened from the
 * "your access is paused" bell notification (window event 'wow:open-suspension').
 * Shows WHY they're paused (features, how long, reason, who) and lets them
 * file ONE appeal (written explanation + optional screenshot) that becomes an
 * admin ticket. Once an admin decides, the outcome shows here. (User ask
 * 2026-10-11.)
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Prohibit, ShieldCheck, Camera, CircleNotch } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FEATURE_LABEL = {
  messaging: "Messaging & DMs",
  stories: "Posting Stories",
  recognition: "High Fives",
  voice: "Walkie / voice",
};

export default function SuspensionAppealModal({ user }) {
  const myId = user?.crew_id || "";
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [text, setText] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!myId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crew/${myId}/suspension`, { params: { actor_id: myId } });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [myId]);

  useEffect(() => {
    const openHandler = () => { setOpen(true); setShowForm(false); setText(""); setScreenshot(null); load(); };
    window.addEventListener("wow:open-suspension", openHandler);
    return () => window.removeEventListener("wow:open-suspension", openHandler);
  }, [load]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!text.trim()) { toast.error("Please explain your appeal."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/crew/${myId}/suspension/appeal`, {
        actor_id: myId, text: text.trim(), screenshot: screenshot || undefined,
      });
      toast.success("Appeal sent to an admin for review.");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send appeal");
    } finally { setBusy(false); }
  };

  const appeal = data?.appeal;
  const until = data?.suspended_until;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="suspension-appeal-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Prohibit weight="bold" className="w-5 h-5 text-red-600" /> Account paused
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-slate-500 italic py-6 text-center">Loading…</p>
        ) : !data?.suspended ? (
          <div className="py-6 text-center space-y-2">
            <ShieldCheck weight="duotone" className="w-10 h-10 text-emerald-500 mx-auto" />
            <p className="text-sm text-slate-600">You're all set — nothing is paused on your account.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-red-700">What's paused</p>
              <div className="flex flex-wrap gap-1.5">
                {(data.features || []).map((f) => (
                  <span key={f} className="text-xs font-semibold bg-white border border-red-200 text-red-700 rounded-full px-2 py-0.5">
                    {FEATURE_LABEL[f] || f}
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-600">
                {until ? `Until ${new Date(until).toLocaleDateString()}` : "Until a manager restores it"}
                {data.suspended_by_name ? ` · by ${data.suspended_by_name}` : ""}
              </p>
              {data.suspend_reason && (
                <p className="text-sm text-slate-800"><span className="font-semibold">Reason: </span>{data.suspend_reason}</p>
              )}
            </div>

            {appeal ? (
              <div className="rounded-xl border border-slate-200 p-3 space-y-1" data-testid="appeal-status">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Your appeal</p>
                {appeal.status === "pending" && <p className="text-sm text-amber-700 font-semibold">Under review by an admin.</p>}
                {appeal.status === "upheld" && <p className="text-sm text-red-700 font-semibold">Reviewed — the pause stays in place.</p>}
                {appeal.status === "overturned" && <p className="text-sm text-emerald-700 font-semibold">Approved — your access was restored.</p>}
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{appeal.appeal_text}</p>
                {appeal.decision_note && <p className="text-xs text-slate-500 mt-1">Admin note: {appeal.decision_note}</p>}
              </div>
            ) : showForm ? (
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Your side of the story</label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Explain why you think this pause isn't fair…"
                  className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:border-blue-500 resize-none"
                  data-testid="appeal-text-input"
                />
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="appeal-screenshot-input" />
                {screenshot ? (
                  <div className="flex items-center gap-2">
                    <img src={screenshot} alt="evidence" className="h-16 rounded-lg border border-slate-200" />
                    <button type="button" onClick={() => setScreenshot(null)} className="text-xs text-red-600 font-semibold">Remove</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-full px-3 py-1.5 hover:border-blue-300"
                    data-testid="appeal-add-screenshot"
                  >
                    <Camera weight="bold" className="w-4 h-4" /> Add screenshot (optional)
                  </button>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setShowForm(false)} className="text-sm font-bold px-3 py-2 rounded-lg text-slate-500">Cancel</button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg bg-[#1D428A] text-white hover:bg-[#16357] disabled:opacity-40"
                    data-testid="appeal-submit"
                  >
                    {busy ? <CircleNotch weight="bold" className="w-4 h-4 animate-spin" /> : null} Send appeal
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="w-full text-sm font-bold px-4 py-2.5 rounded-lg bg-[#1D428A] text-white hover:bg-[#16357]"
                data-testid="appeal-start"
              >
                Appeal this pause
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
